import { useEffect, useState, type CSSProperties } from 'react';
import type { ClientRoomState, SpotifyAuthStatus } from '@hitster/shared';
import { useT } from '../i18n.tsx';
import { Alert, Countdown, Spinner, Tokens } from '../components/common.tsx';
import { PlayerList } from '../components/PlayerList.tsx';
import { RevealCard } from '../components/RevealCard.tsx';
import { PhaseTimer } from '../components/PhaseTimer.tsx';
import { Podium } from '../components/Podium.tsx';
import { Turntable } from '../components/Turntable.tsx';
import { SpeakerMenu } from '../components/SpeakerMenu.tsx';
import { Deck } from '../components/Deck.tsx';
import { RoomMenu } from '../components/RoomMenu.tsx';
import { useCoverColor } from '../hooks/useCoverColor.ts';
import { IconReplay } from '../components/Icon.tsx';
import { Timeline } from '../components/Timeline.tsx';
import type { RoomApi } from '../net/useRoom.ts';

/**
 * Die Steuerungs-Ansicht -- gedacht fuer das Handy jedes Spielers.
 *
 * Nur der aktive Spieler bzw. im Klau-Fenster die anderen bekommen
 * Interaktionen; alle anderen sehen den Verlauf mit.
 */
export function GamePage({
  state,
  api,
  authStatus,
}: {
  state: ClientRoomState;
  api: RoomApi;
  authStatus: SpotifyAuthStatus | null;
}) {
  const t = useT();

  const you = state.players.find((player) => player.id === state.youId) ?? null;
  const active = state.players.find((player) => player.id === state.activePlayerId) ?? null;
  const isYourTurn = state.activePlayerId === state.youId;
  const isHost = state.hostId === state.youId;

  /**
   * Der Spieler, dessen Zug dieser Client gerade bedient.
   *
   * Normalfall: man selbst. Beim Spielen an einem Bildschirm handelt
   * der Host zusaetzlich fuer lokale Mitspieler -- dann muss die
   * Platzierungs-Oberflaeche DEREN Zeitleiste zeigen, nicht die des
   * Hosts. Sonst legt man Karten in die falsche Reihe.
   */
  const controlled = isYourTurn ? you : isHost && active?.isLocal ? active : null;

  /** Wird bei Aktionen mitgeschickt, wenn fuer jemand anderen gehandelt wird. */
  const onBehalfOf = controlled && controlled.id !== state.youId ? controlled.id : undefined;

  /** Lokale Mitspieler, fuer die der Host im Klau-Fenster handeln darf. */
  const localStealers =
    isHost && state.phase === 'stealing'
      ? state.players.filter(
          (player) =>
            player.isLocal && player.id !== state.activePlayerId && player.tokens > 0,
        )
      : [];

  // Lokal gewaehlter Slot. Erst mit "Bestätigen" wird daraus ein Zug.
  const [slot, setSlot] = useState<number | null>(null);
  const [stealSlot, setStealSlot] = useState<number | null>(null);
  /** Gewaehlte Klau-Position je lokalem Mitspieler (Pass-and-Play). */
  const [localStealSlots, setLocalStealSlots] = useState<Record<string, number>>({});
  const [guessTitle, setGuessTitle] = useState('');
  const [guessArtist, setGuessArtist] = useState('');

  const cardId = state.currentCard?.revealed === false ? state.currentCard.cardId : null;
  const revealed = state.phase === 'reveal' || state.phase === 'finished';

  // Bei jeder neuen Karte alle lokalen Eingaben zurücksetzen.
  useEffect(() => {
    setSlot(null);
    setStealSlot(null);
    setGuessTitle('');
    setGuessArtist('');
  }, [cardId]);

  // Serverseitige Platzierung gewinnt (z.B. nach Reconnect).
  useEffect(() => {
    if (controlled && state.activePlacement) setSlot(state.activePlacement.slotIndex);
  }, [controlled, state.activePlacement]);

  const yourClaim = state.stealClaims.find((claim) => claim.playerId === state.youId) ?? null;
  const youPassed = state.stealPasses.includes(state.youId);

  // ---------------------------------------------------------------------
  // Aktionen
  // ---------------------------------------------------------------------

  const choose = (slotIndex: number) => {
    setSlot(slotIndex);
    // Direkt an den Server, damit die TV-Ansicht mitzieht.
    api.place(slotIndex, onBehalfOf);
  };

  const submitGuess = () => {
    if (guessTitle.trim().length === 0 && guessArtist.trim().length === 0) return;
    api.guess({ title: guessTitle, artist: guessArtist }, onBehalfOf);
  };

  /**
   * Die Coverfarbe der aufgedeckten Karte faerbt den GANZEN Raum, nicht
   * nur die Karte. Auf dem Fernseher ist das der eigentliche Moment:
   * die Wand nimmt kurz die Farbe des Songs an. Vor dem Aufdecken
   * bewusst nichts -- sonst waere die Farbe ein Hinweis.
   */
  const coverColor = useCoverColor(revealed ? state.lastResult?.card.coverUrl : null);

  return (
    <div
      className={`page ${revealed && coverColor ? 'page--lit' : ''}`}
      style={coverColor ? ({ '--cover': coverColor } as CSSProperties) : undefined}
    >
      {/* ---------------- Kopfzeile ---------------- */}
      <div className="row row--between">
        <div className="row" style={{ gap: '0.5rem' }}>
          <span className="badge">{state.code}</span>
          <Deck remaining={state.deckRemaining} />
        </div>
        <div className="row" style={{ gap: '0.25rem' }}>
          {you && state.settings.maxTokens > 0 && (
            <Tokens count={you.tokens} max={state.settings.maxTokens} />
          )}
          <Countdown deadline={state.phaseDeadline} />

          {/* Ton kompakt in der Kopfzeile statt als grosses Panel am
              Seitenende -- sonst muss man fuer die eigene Zeitleiste
              scrollen. */}
          <SpeakerMenu state={state} api={api} authStatus={authStatus} />

          <RoomMenu state={state} api={api} />
        </div>
      </div>

      {/**
       * Der Moment, groß.
       *
       * Vorher war das eine h2 zwischen gleichrangigen Kästen -- man
       * musste lesen, um zu wissen, wer dran ist. Jetzt trägt die
       * Typografie die Hierarchie: eine kleine Zeile Kontext, darunter
       * der Name in Displaygröße. Das funktioniert auch aus fünf Metern.
       */}
      <header className="hero">
        <span className="hero__eyebrow">
          {state.phase === 'playing' && t('game.listening')}
          {state.phase === 'stealing' && t('steal.window')}
          {state.phase === 'reveal' && t('reveal.next')}
          {state.phase === 'finished' && t('finished.title')}
        </span>
        <h1 className="hero__title">
          {isYourTurn ? t('game.yourTurn') : t('game.turnOf', { name: active?.name ?? '—' })}
        </h1>
      </header>

      {/* Beim Spielen an einem Bildschirm muss unmissverständlich sein,
          für wen gerade gelegt wird — sonst platziert jemand für den
          Falschen. */}
      {onBehalfOf && active && (
        <Alert kind="info">{t('game.actingFor', { name: active.name })}</Alert>
      )}

      {state.audioDeviceCount === 0 && <Alert kind="warning">{t('audio.none')}</Alert>}

      {/* ================= PHASE: playing ================= */}
      {state.phase === 'playing' && (
        <>
          {controlled ? (
            <div className="stack">
              {/* Zeigt, dass etwas laeuft, ohne den Song zu verraten. */}
              <Turntable />

              <div className="panel">
                <div className="panel__title">{t('game.placePrompt')}</div>
                {/* Die Zeitleiste des BEDIENTEN Spielers, nicht die
                    eigene -- beim Pass-and-Play sind das verschiedene. */}
                <Timeline
                  cards={controlled.timeline}
                  ownerId={controlled.id}
                  interactive
                  selectedSlot={slot}
                  onSelectSlot={choose}
                  live
                />
                {slot !== null && (
                  <span className="text-xs text-dim">{t('game.placementSet')}</span>
                )}
              </div>

              {/* Titel/Interpret raten */}
              {state.settings.allowTitleArtistGuess && (
                <div className="panel stack stack--tight">
                  <div className="panel__title">{t('game.guessHint')}</div>
                  {state.guessSubmitted ? (
                    <Alert kind="success">{t('game.guessSubmitted')}</Alert>
                  ) : state.guessChoices.length > 0 ? (
                    /**
                     * Auswahl-Modus.
                     *
                     * Die Liste ist leer, wenn der Modus aus ist ODER die
                     * Playlist zu klein fuer Ablenker war -- dann faellt
                     * es von selbst auf Freitext zurueck.
                     */
                    <div className="stack stack--tight">
                      {state.guessChoices.map((choice) => (
                        <button
                          key={`${choice.title}|${choice.artists.join(',')}`}
                          type="button"
                          className="btn btn--block guess-choice"
                          onClick={() =>
                            api.guess(
                              { title: choice.title, artist: choice.artists.join(', ') },
                              onBehalfOf,
                            )
                          }
                        >
                          <span className="guess-choice__title">{choice.title}</span>
                          <span className="guess-choice__artist">
                            {choice.artists.join(', ')}
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <>
                      <input
                        className="input"
                        placeholder={t('game.guessTitle')}
                        value={guessTitle}
                        onChange={(event) => setGuessTitle(event.target.value)}
                      />
                      <input
                        className="input"
                        placeholder={t('game.guessArtist')}
                        value={guessArtist}
                        onChange={(event) => setGuessArtist(event.target.value)}
                      />
                      <button type="button" className="btn" onClick={submitGuess}>
                        {t('game.guessSubmit')}
                      </button>
                    </>
                  )}
                </div>
              )}

              <div className="stack stack--tight">
                <button
                  type="button"
                  className="btn btn--primary btn--lg btn--block"
                  disabled={slot === null || state.resolvingYear}
                  onClick={() => api.commit(onBehalfOf)}
                >
                  {/* Ohne diese Rueckmeldung wirkt der Klick wirkungslos,
                      solange der Server auf das gepruefte Jahr wartet. */}
                  {state.resolvingYear ? (
                    <>
                      <Spinner />
                      {t('game.checkingYear')}
                    </>
                  ) : (
                    t('game.confirmPlacement')
                  )}
                </button>

                <div className="row" style={{ gap: '0.5rem' }}>
                  <button
                    type="button"
                    className="btn"
                    style={{ flex: 1 }}
                    onClick={() => api.replay()}
                    title={t('game.replayHint')}
                  >
                    <IconReplay />
                    {t('game.replay')}
                  </button>

                  {state.settings.allowSkip && (
                    <button
                      type="button"
                      className="btn"
                      style={{ flex: 1 }}
                      // Die Token des BEDIENTEN Spielers zaehlen, nicht die eigenen.
                      disabled={controlled.tokens < 1}
                      onClick={() => api.skip(onBehalfOf)}
                    >
                      {t('game.skip')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="panel stack stack--tight">
              {/* Auch wer nur zusieht, soll sehen, dass Musik laeuft. */}
              <Turntable />
              <span className="text-muted">{t('game.listening')}</span>
              <span className="text-sm text-dim">
                {t('game.waitingForPlacement', { name: active?.name ?? '—' })}
              </span>
              {/* Auch wer nicht am Zug ist, will den Song nochmal hoeren. */}
              <button type="button" className="btn" onClick={() => api.replay()}>
                <IconReplay />
                {t('game.replay')}
              </button>
              {active && (
                <>
                  <div className="panel__title">{active.name}</div>
                  {/*
                   * `live` auch hier: dass Musik laeuft, gilt fuer alle
                   * im Raum, nicht nur fuer den, der platziert.
                   *
                   * Und `selectedSlot`: man sieht mit, wohin er
                   * zusteuert -- das ist der spannende Teil.
                   */}
                  <Timeline
                    cards={active.timeline}
                    ownerId={active.id}
                    selectedSlot={state.activePlacement?.slotIndex ?? null}
                    compact
                    live
                  />
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* ================= PHASE: stealing ================= */}
      {state.phase === 'stealing' && (
        <div className="stack">
          <div className="panel stack stack--tight">
            <div className="panel__title">{t('steal.window')}</div>

            {/* Die einzige Phase, in der es wirklich um Sekunden geht. */}
            <PhaseTimer
              deadline={state.phaseDeadline}
              totalMs={state.settings.stealWindowSec * 1000}
              label={t('steal.timeLeft')}
            />

            <span>{t('steal.explain', { name: active?.name ?? '—' })}</span>

            {/* Die Platzierung des aktiven Spielers ist jetzt oeffentlich. */}
            {active && (
              <Timeline
                cards={active.timeline}
                ownerId={active.id}
                selectedSlot={state.activePlacement?.slotIndex ?? null}
                compact
              />
            )}
          </div>

          {isYourTurn ? (
            <Alert kind="info">{t('steal.waiting')}</Alert>
          ) : yourClaim ? (
            <Alert kind="success">{t('steal.claimed')}</Alert>
          ) : youPassed ? (
            <Alert kind="info">{t('steal.passed')}</Alert>
          ) : !you || you.tokens < 1 || !state.settings.allowSteal ? (
            <Alert kind="info">{t('steal.noTokens')}</Alert>
          ) : (
            <div className="stack">
              <div className="panel">
                <div className="panel__title">{t('steal.claimAt')}</div>
                {/* Geklaut wird in die EIGENE Zeitleiste. */}
                <Timeline
                  cards={you.timeline}
                  ownerId={you.id}
                  interactive
                  selectedSlot={stealSlot}
                  onSelectSlot={setStealSlot}
                />
              </div>
              <div className="row" style={{ gap: '0.5rem' }}>
                <button
                  type="button"
                  className="btn btn--primary"
                  style={{ flex: 1 }}
                  disabled={stealSlot === null}
                  onClick={() => stealSlot !== null && api.claimSteal(stealSlot)}
                >
                  {t('steal.claim')}
                </button>
                <button type="button" className="btn" style={{ flex: 1 }} onClick={() => api.passSteal()}>
                  {t('steal.pass')}
                </button>
              </div>
            </div>
          )}

          {/* Klauen fuer lokale Mitspieler -- die haben kein eigenes
              Geraet, also entscheidet der Host fuer jeden einzeln. */}
          {localStealers.map((player) => {
            const claimed = state.stealClaims.some((claim) => claim.playerId === player.id);
            const passed = state.stealPasses.includes(player.id);

            return (
              <div key={player.id} className="panel stack stack--tight">
                <div className="panel__title">
                  {player.name} · {t('game.tokens')}: {player.tokens}
                </div>

                {claimed ? (
                  <Alert kind="success">{t('steal.claimed')}</Alert>
                ) : passed ? (
                  <Alert kind="info">{t('steal.passed')}</Alert>
                ) : (
                  <>
                    <Timeline
                      cards={player.timeline}
                      ownerId={player.id}
                      interactive
                      selectedSlot={localStealSlots[player.id] ?? null}
                      onSelectSlot={(slotIndex) =>
                        setLocalStealSlots((previous) => ({ ...previous, [player.id]: slotIndex }))
                      }
                    />
                    <div className="row" style={{ gap: '0.5rem' }}>
                      <button
                        type="button"
                        className="btn btn--primary"
                        style={{ flex: 1 }}
                        disabled={localStealSlots[player.id] === undefined}
                        onClick={() => {
                          const chosen = localStealSlots[player.id];
                          if (chosen !== undefined) api.claimSteal(chosen, player.id);
                        }}
                      >
                        {t('steal.claim')}
                      </button>
                      <button
                        type="button"
                        className="btn"
                        style={{ flex: 1 }}
                        onClick={() => api.passSteal(player.id)}
                      >
                        {t('steal.pass')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}

          {/* Wer sonst noch klaut */}
          {state.stealClaims
            .filter((claim) => claim.playerId !== state.youId)
            .map((claim) => (
              <span key={claim.playerId} className="text-sm text-dim">
                {t('steal.someoneClaimed', {
                  name: state.players.find((p) => p.id === claim.playerId)?.name ?? '—',
                })}
              </span>
            ))}
        </div>
      )}

      {/* ================= PHASE: reveal ================= */}
      {state.phase === 'reveal' && state.lastResult && (
        <div className="stack">
          <RevealCard result={state.lastResult} state={state} />
          {(isHost || isYourTurn) && !state.settings.autoAdvance && (
            <button
              type="button"
              className="btn btn--primary btn--lg btn--block"
              onClick={api.advance}
            >
              {t('reveal.next')}
            </button>
          )}
        </div>
      )}

      {/* ================= PHASE: finished ================= */}
      {state.phase === 'finished' && (
        <div className="stack">
          <div className="panel text-center stack">
            <h1>{t('finished.title')}</h1>
            <h2>
              {state.winnerIds.length === 1
                ? t('finished.winner', {
                    name: state.players.find((p) => p.id === state.winnerIds[0])?.name ?? '—',
                  })
                : t('finished.winners', {
                    names: state.winnerIds
                      .map((id) => state.players.find((p) => p.id === id)?.name ?? '—')
                      .join(', '),
                  })}
            </h2>
          </div>

          {/* Endstand nach Karten -- die Spielerliste weiter unten steht
              in Beitrittsreihenfolge und beantwortet das nicht. */}
          <div className="panel">
            <Podium state={state} />
          </div>

          {isHost && (
            <button
              type="button"
              className="btn btn--primary btn--lg btn--block"
              onClick={api.restartGame}
            >
              {t('finished.backToLobby')}
            </button>
          )}
        </div>
      )}

      {/* ---------------- Deine Zeitleiste ---------------- */}
      {you && state.phase !== 'lobby' && (
        <div className="panel">
          <div className="panel__title">
            {t('game.timeline')} ·{' '}
            {t('game.cards', {
              count: you.timeline.length,
              target: state.settings.targetCards,
            })}
          </div>
          <Timeline
            cards={you.timeline}
            ownerId={you.id}
            highlightCardId={state.lastResult?.awardedTo === you.id ? state.lastResult.card.cardId : null}
          />
        </div>
      )}

      {/* ---------------- Punktestand ---------------- */}
      <div className="panel">
        <div className="panel__title">{t('lobby.players')}</div>
        {/*
         * Aufklappbar: Am Handy sieht man sonst nur die eigene Leiste
         * und die des aktiven Spielers. Ob sich ein Klau lohnt, haengt
         * aber davon ab, wie die anderen stehen.
         */}
        <PlayerList state={state} expandable />
      </div>
    </div>
  );
}
